/**
 * 统一确认框组件
 *
 * @description 基于渠道管理设计的统一确认框样式
 * @version 1.0.0
 * @author AI Assistant
 * @date 2025-09-24
 */

import React from 'react';
import { Modal, App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

interface UnifiedConfirmProps {
  title: string;
  content: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  confirmLoading?: boolean;
  okText?: string;
  cancelText?: string;
  type?: 'warning' | 'error' | 'info';
}

// Hook 版本的确认框
export const useUnifiedConfirm = () => {
  const { modal } = App.useApp();
  const { t } = useTranslation();

  const show = ({
    title,
    content,
    onConfirm,
    onCancel,
    confirmLoading = false,
    okText,
    cancelText,
    type = 'warning',
  }: UnifiedConfirmProps) => {
    const resolvedOkText = okText || t('common:action.confirm');
    const resolvedCancelText = cancelText || t('common:action.cancel');
    const iconMap = {
      warning: (
        <ExclamationCircleOutlined
          style={{ color: '#faad14', fontSize: '22px' }}
        />
      ),
      error: (
        <ExclamationCircleOutlined
          style={{ color: '#ff4d4f', fontSize: '22px' }}
        />
      ),
      info: (
        <ExclamationCircleOutlined
          style={{ color: '#1890ff', fontSize: '22px' }}
        />
      ),
    };

    modal.confirm({
      title: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '16px',
            fontWeight: 600,
            color: '#262626',
          }}
        >
          {iconMap[type]}
          <span>{title}</span>
        </div>
      ),
      content: (
        <div
          style={{
            marginTop: '16px',
            marginLeft: '34px',
            fontSize: '14px',
            color: '#595959',
            lineHeight: '1.5',
          }}
        >
          {content}
        </div>
      ),
      onOk: onConfirm,
      onCancel,
      okText: resolvedOkText,
      cancelText: resolvedCancelText,
      centered: true,
      width: 420,
      styles: {
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
        },
        body: {
          padding: '24px',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
        },
        header: {
          background: 'transparent',
          borderBottom: 'none',
          paddingBottom: '0',
        },
        footer: {
          background: 'transparent',
          borderTop: '1px solid rgba(16, 185, 129, 0.1)',
          paddingTop: '16px',
          marginTop: '24px',
        },
      },
      okButtonProps: {
        style: {
          background:
            type === 'error'
              ? 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)'
              : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          border: 'none',
          borderRadius: '6px',
          height: '36px',
          fontWeight: 500,
          boxShadow:
            type === 'error'
              ? '0 2px 8px rgba(255, 77, 79, 0.3)'
              : '0 2px 8px rgba(16, 185, 129, 0.3)',
          minWidth: '80px',
        },
      },
      cancelButtonProps: {
        style: {
          borderRadius: '6px',
          height: '36px',
          fontWeight: 500,
          minWidth: '80px',
          borderColor: '#10b981',
          color: '#10b981',
        },
      },
    });
  };

  return { show };
};

// 静态版本作为后备（会有警告但仍可使用）
const UnifiedConfirm = {
  show: ({
    title,
    content,
    onConfirm,
    onCancel,
    confirmLoading = false,
    okText,
    cancelText,
    type = 'warning',
  }: UnifiedConfirmProps) => {
    const isZh = i18n.language === 'zh-CN';
    const resolvedOkText = okText || (isZh ? '确认' : 'Confirm');
    const resolvedCancelText = cancelText || (isZh ? '取消' : 'Cancel');
    const iconMap = {
      warning: (
        <ExclamationCircleOutlined
          style={{ color: '#faad14', fontSize: '22px' }}
        />
      ),
      error: (
        <ExclamationCircleOutlined
          style={{ color: '#ff4d4f', fontSize: '22px' }}
        />
      ),
      info: (
        <ExclamationCircleOutlined
          style={{ color: '#1890ff', fontSize: '22px' }}
        />
      ),
    };

    Modal.confirm({
      title: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '16px',
            fontWeight: 600,
            color: '#262626',
          }}
        >
          {iconMap[type]}
          <span>{title}</span>
        </div>
      ),
      content: (
        <div
          style={{
            marginTop: '16px',
            marginLeft: '34px',
            fontSize: '14px',
            color: '#595959',
            lineHeight: '1.5',
          }}
        >
          {content}
        </div>
      ),
      onOk: onConfirm,
      onCancel,
      okText: resolvedOkText,
      cancelText: resolvedCancelText,
      centered: true,
      width: 420,
      styles: {
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
        },
        body: {
          padding: '24px',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
        },
        header: {
          background: 'transparent',
          borderBottom: 'none',
          paddingBottom: '0',
        },
        footer: {
          background: 'transparent',
          borderTop: '1px solid rgba(16, 185, 129, 0.1)',
          paddingTop: '16px',
          marginTop: '24px',
        },
      },
      okButtonProps: {
        style: {
          background:
            type === 'error'
              ? 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)'
              : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          border: 'none',
          borderRadius: '6px',
          height: '36px',
          fontWeight: 500,
          boxShadow:
            type === 'error'
              ? '0 2px 8px rgba(255, 77, 79, 0.3)'
              : '0 2px 8px rgba(16, 185, 129, 0.3)',
          minWidth: '80px',
        },
      },
      cancelButtonProps: {
        style: {
          borderRadius: '6px',
          height: '36px',
          fontWeight: 500,
          minWidth: '80px',
          borderColor: '#10b981',
          color: '#10b981',
        },
      },
    });
  },
};

export default UnifiedConfirm;
